import { getToken } from 'serviceWorker/services/config';
import { getHustroClientHeaders } from 'shared/apiHeaders';
import { MIME_TYPES } from 'shared/constants/MIME_TYPES';
import { frontendVersionWithBuildNumber } from 'shared/version';

/**
 * The client headers of the worker's own build: the version is that of the
 * code that sends the request, never the one a page stored in the config (an
 * old tab would make the API answer 498 to a current worker). The one place
 * the worker writes them.
 */
export function getWorkerClientHeaders(): Record<string, string> {
  return getHustroClientHeaders(frontendVersionWithBuildNumber);
}

/**
 * The Authorization header of the session token in memory; none without a
 * token (`pull` does not send a session request without it). The one place
 * the worker writes this header.
 */
export function getSessionAuthorizationHeader(): { Authorization?: string } {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * The headers of every JSON request of the worker to the API: the language
 * when the config has one (at sign-in the user is downloaded before the page
 * sent it), the client headers of the worker's own build and the session
 * token when there is one (`pull` does not send a session request without it).
 */
export function buildWorkerRequestHeaders(locale?: string): Record<string, string> {
  return {
    'Content-Type': MIME_TYPES.JSON,
    ...(locale && { 'Accept-Language': locale }),
    ...getWorkerClientHeaders(),
    ...getSessionAuthorizationHeader(),
  };
}

export function hasAuthorizationHeader(headers?: HeadersInit): boolean {
  if (!headers) {
    return false;
  }

  if (headers instanceof Headers) {
    return headers.has('Authorization');
  }

  if (Array.isArray(headers)) {
    return headers.some(
      ([key, value]) =>
        key.toLowerCase() === 'authorization' && value != null && value !== ''
    );
  }

  return Object.entries(headers).some(
    ([key, value]) =>
      key.toLowerCase() === 'authorization' && value != null && value !== ''
  );
}
